import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { functions } from '../functions/_functions';
import { withTheme } from '../core/theming';

import Div from './Div';
import Icon from './Icon';

const MenuItem = styled.li`
  display: list-item;
  margin: 0;
  padding: 0;
  white-space: nowrap;
`;

const MenuLink = withTheme(styled(Link)`
  cursor: pointer;
  text-decoration: none !important;
  padding: ${(props) => (props.padding ? props.padding : '12px 5px 12px 10px')};
  position: relative;
  margin: 0;
  display: flex;
  background-color: transparent;
  align-items: center;
  ${(props) => props.textColor && functions.makeResponsive('color', props.theme.colors[props.textColor])};
  ${(props) => props.textSize && functions.makeResponsiveFontSize(props.textSize, props.theme.textSize)};
  transition: all 0.1s ease;
  border-left: ${(props) => (props.leftBorder ? '4px solid transparent' : 'none')};
  ${(props) =>
    props.highlighted
      ? props.hoverBg && functions.makeResponsiveProperty(props.hoverBg, 'background', props.theme.colors)
      : props.bg && functions.makeResponsiveProperty(props.bg, 'background', props.theme.colors)};

  &:active,
  &:focus,
  &:hover {
    outline: none !important;
    text-decoration: none !important;
    border-left: ${(props) => (props.leftBorder ? '4px solid white' : 'none')};
    ${(props) => props.bg && functions.makeResponsiveProperty(props.hoverBg, 'background', props.theme.colors)};
  }
`);

MenuLink.defaultProps = {
  leftBorder: true,
};

const Header = withTheme(styled.li`
  display: ${(props) => (props.mini ? 'none !important' : 'list-item')};
  position: relative;
  margin: 0;
  padding: 10px 25px 10px 15px;
  ${(props) => props.textSize && functions.makeResponsiveFontSize(props.textSize, props.theme.textSize)};
  ${(props) => props.textColor && functions.makeResponsive('color', props.theme.colors[props.textColor])};
  ${(props) => props.bg && functions.makeResponsiveProperty(props.bg, 'background', props.theme.colors)};
  ${(props) => props.fontFamily && `font-family: ${props.theme.fontFamily[props.fontFamily]}`};
`);

const Group = withTheme(styled.li`
  cursor: pointer;
  text-decoration: none !important;
  padding: 12px 5px 12px 10px;
  position: relative;
  margin: 0;
  background-color: transparent;
  align-items: center;
  display: flex;
  ${(props) => props.textColor && functions.makeResponsive('color', props.theme.colors[props.textColor])};
  transition: all 0.1s ease;
  border-left: 4px solid transparent;

  &:active,
  &:focus,
  &:hover {
    outline: none !important;
    text-decoration: none !important;
    border-left: 4px solid white;
    ${(props) => props.bg && functions.makeResponsiveProperty(props.bg, 'background', props.theme.colors)};
  }
`);

const OptionsPopup = withTheme(styled.li`
  ${(props) => `transform: translate(${props.theme.application.miniMenuWidth}, -54px)`};
  margin: 0px 0px 0px 10px;
  position: absolute;
  overflow: visible;
  z-index: 10;
  transition: all 0.1s ease;
  ${(props) => props.rounded && functions.findRoundedDirection(props.rounded, props.theme.rounded)};
  ${(props) => props.bg && functions.makeResponsiveProperty(props.bg, 'background', props.theme.colors)};
`);

const Options = withTheme(styled.ul`
  list-style: none;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;

  &:hover {
    overflow: visible;
  }
  ${(props) => props.p && functions.findDirection('padding', props.p)};
  ${(props) => props.m && functions.findDirection('margin', props.m)};
  ${(props) => props.textColor && functions.makeResponsive('color', props.theme.colors[props.textColor])};
`);

const SubMenu = ({ icon, name, options, iconsStyle, itemsStyle, optionsStyle, subMenuPopupStyle, mini }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isChildHovered, setChildHovered] = useState(false);
  return (
    <>
      <MenuItem>
        <Group
          {...itemsStyle}
          onMouseEnter={() => {
            setIsHovered(true);
          }}
          onMouseLeave={() => {
            setTimeout(() => {
              setIsHovered(false);
            }, 200);
          }}
          onClick={() => {
            setIsOpen(!isOpen);
          }}
        >
          <Icon name={icon} {...iconsStyle} />
          {name}
          {!mini ? (
            isOpen ? (
              <Icon name="DownArrow" {...iconsStyle} pos="absolute" right="0" />
            ) : (
              <Icon name="UpArrow" {...iconsStyle} pos="absolute" right="0" />
            )
          ) : null}
        </Group>
      </MenuItem>
      {isOpen && !mini ? (
        <Options {...optionsStyle}>
          {options.map(
            (option) =>
              ({
                ITEM: (
                  <MenuItem>
                    <MenuLink
                      padding="5px 5px 5px 60px"
                      textSize="subheader"
                      highlighted={typeof window !== 'undefined' && window.location.pathname === option.link}
                      to={option.link}
                      {...itemsStyle}
                    >
                      {option.name}
                    </MenuLink>
                  </MenuItem>
                ),
              })[option.type]
          )}
        </Options>
      ) : null}
      {mini && (isHovered || isChildHovered) ? (
        <OptionsPopup
          onMouseEnter={() => {
            setChildHovered(true);
          }}
          onMouseLeave={() => {
            setChildHovered(false);
          }}
          rounded="md"
          {...subMenuPopupStyle}
        >
          <Options {...optionsStyle}>
            {options.map(
              (option) =>
                ({
                  ITEM: (
                    <MenuItem>
                      <MenuLink
                        padding="5px 40px"
                        leftBorder={false}
                        textSize="subheader"
                        highlighted={typeof window !== 'undefined' && window.location.pathname === option.link}
                        {...itemsStyle}
                        to={option.link}
                      >
                        {option.name}
                      </MenuLink>
                    </MenuItem>
                  ),
                })[option.type]
            )}
          </Options>
        </OptionsPopup>
      ) : null}
    </>
  );
};

SubMenu.propTypes = {
  theme: PropTypes.shape({
    application: {},
  }).isRequired,
  mini: PropTypes.bool.isRequired,
  icon: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      type: PropTypes.oneOf(['HEADER', 'ITEM']),
      icon: PropTypes.string,
      link: PropTypes.string,
    })
  ).isRequired,
  iconsStyle: PropTypes.shape({}).isRequired,
  itemsStyle: PropTypes.shape({}).isRequired,
  optionsStyle: PropTypes.shape({}).isRequired,
  subMenuPopupStyle: PropTypes.shape({}).isRequired,
};

const Menu = ({
  mini,
  menuStyle,
  subMenuPopupStyle,
  optionsStyle,
  itemsStyle,
  logo,
  logoStyle,
  iconsStyle,
  headersStyle,
  options,
  theme,
}) => (
  <Div
    top="0"
    left="0"
    minH="100%"
    h="100%"
    minW={mini ? theme.application.miniMenuWidth : theme.application.menuWidth}
    w={mini ? theme.application.miniMenuWidth : theme.application.menuWidth}
    transition={theme.application.menuTransition}
    {...menuStyle}
  >
    <Options {...optionsStyle}>
      <li>
        <Div
          minW={mini ? theme.application.miniMenuWidth : theme.application.menuWidth}
          w={mini ? theme.application.miniMenuWidth : theme.application.menuWidth}
          minH={theme.application.headerHeight}
          h={theme.application.headerHeight}
          d="inline-flex"
          justify="center"
          align="center"
          {...logoStyle}
        >
          <Div tag="span">{mini ? logo.smallLogo : logo.largeLogo}</Div>
        </Div>
      </li>
      {options.map(
        (option) =>
          ({
            HEADER: (
              <Header mini={mini} {...headersStyle}>
                {option.name.toUpperCase()}
              </Header>
            ),
            ITEM: (
              <MenuItem>
                <MenuLink
                  highlighted={typeof window !== 'undefined' && window.location.pathname === option.link}
                  {...itemsStyle}
                  to={option.link}
                >
                  <Icon name={option.icon} {...iconsStyle} />
                  {option.name}
                </MenuLink>
              </MenuItem>
            ),
            SUB_MENU: (
              <SubMenu
                icon={option.icon}
                name={option.name}
                options={option.options}
                iconsStyle={iconsStyle}
                itemsStyle={itemsStyle}
                optionsStyle={optionsStyle}
                subMenuPopupStyle={subMenuPopupStyle}
                mini={mini}
              />
            ),
          })[option.type]
      )}
    </Options>
  </Div>
);

Menu.propTypes = {
  theme: PropTypes.shape({
    application: {},
  }).isRequired,
  mini: PropTypes.bool,
  menuStyle: PropTypes.shape({}),
  subMenuPopupStyle: PropTypes.shape({}),
  optionsStyle: PropTypes.shape({}),
  iconsStyle: PropTypes.shape({}),
  headersStyle: PropTypes.shape({}),
  itemsStyle: PropTypes.shape({}),
  logoStyle: PropTypes.shape({}),
  logo: PropTypes.shape({
    largeLogo: PropTypes.string,
    smallLogo: PropTypes.string,
  }),
  options: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      type: PropTypes.oneOf(['HEADER', 'ITEM']),
      icon: PropTypes.string,
      link: PropTypes.string,
    })
  ),
};

Menu.defaultProps = {
  mini: false,
  menuStyle: {
    pos: 'absolute',
    bg: 'brand500',
  },
  subMenuPopupStyle: {
    bg: 'brand500',
  },
  optionsStyle: {
    textColor: 'white',
    p: { y: '8px', x: '0px' },
  },
  headersStyle: {
    textSize: 'caption',
    textColor: 'brand300',
    bg: 'brand700',
  },
  logoStyle: {
    textColor: 'gray100',
    bg: 'brand500',
  },
  logo: {
    largeLogo: 'Onpoint',
    smallLogo: 'OP',
  },
  itemsStyle: {
    textColor: 'gray100',
    bg: 'brand500',
    hoverBg: 'brand700',
  },
  iconsStyle: {
    color: 'gray100',
    p: { r: '16px' },
  },
  options: [],
};

export default withTheme(Menu);
